import React, { useState } from 'react'
import isEmpty from 'lodash/isEmpty'
import { Modal, ModalDragHandle } from '@ztechapps/webui.modal'
import { ModalBody, Modal as ModalCore } from 'react-bootstrap'
import { SaveCancelButtons } from '@ztechapps/webui.button'
import { TextAreaInput } from '@ztechapps/webui.input'
import { Alert, HorizontalStack, NullComponent } from '@ztechapps/webui.util'
import { useError, useToast } from '@ztechapps/common.hook'
import { useMarkOrderNotReceivable } from './useMarkOrderNotReceivable'

export type OrderMarkNotReceivableModalProps = {
  orderId: string
  show: boolean
  onHide: () => void
  // Refresh the affected order/list after a successful mark.
  onSuccess?: () => void | Promise<void>
}

export const OrderMarkNotReceivableModal = ({ orderId, show, onHide, onSuccess }: OrderMarkNotReceivableModalProps) => {
  const [reason, setReason] = useState('')
  const [markNotReceivable, { loading, error }] = useMarkOrderNotReceivable()
  const { showSuccess } = useToast()
  useError(error)

  const isReasonEmpty = isEmpty(reason.trim())

  const handleCancel = () => {
    setReason('')
    onHide()
  }

  const handleSave = async () => {
    if (isReasonEmpty) {
      return
    }
    await markNotReceivable({ orderId, value: true, reason: reason.trim() })
    showSuccess({ message: 'Order marked as Not Receivable.' })
    setReason('')
    await onSuccess?.()
    onHide()
  }

  return (
    <Modal show={show} onHide={onHide} size="md" trigger={NullComponent}>
      <ModalDragHandle>Mark Order As Not Receivable</ModalDragHandle>
      <ModalBody>
        <Alert type="warning" className="mb-3">
          This marks the order uncollectible and excludes it from the accounts-receivable totals. A reason is required
          and will be recorded in the order activity log.
        </Alert>
        <label className="form-label fw-semibold" htmlFor="not-receivable-reason">
          Reason
        </label>
        <TextAreaInput
          id="not-receivable-reason"
          rows={4}
          value={reason}
          submitting={loading}
          placeholder="Explain why this order is not receivable..."
          onChange={(event) => setReason(event.target.value)}
        />
      </ModalBody>
      <ModalCore.Footer>
        <HorizontalStack gap={2} className="justify-content-end">
          <SaveCancelButtons
            onSave={handleSave}
            onCancel={handleCancel}
            disabled={isReasonEmpty}
            isBusy={loading}
            saveText="Mark As Not Receivable"
            saveIcon="ban"
            cancelText="Cancel"
          />
        </HorizontalStack>
      </ModalCore.Footer>
    </Modal>
  )
}
